import { AlertDialog as BaseAlertDialog } from '@base-ui-components/react/alert-dialog';
import { cn } from '../cn.js';

/**
 * A dialog that interrupts, for a decision that cannot be taken back.
 *
 * **Not a Dialog.** A dialog is dismissed by the escape key, by the backdrop, and by anything else
 * a person does to leave; an alert dialog is not, because closing it by accident is the same
 * gesture as answering it. Base UI enforces that difference — it is the whole reason the two
 * components exist separately — so the choice between them is a choice about what a stray click
 * means, not about how it looks.
 *
 * Use it where the answer is irreversible: deleting a tenant, purging recordings, revoking a
 * grant. `ConfirmDestructive` is built on this and is what feature code should reach for.
 */
export const AlertDialog = BaseAlertDialog.Root;
export const AlertDialogTrigger = BaseAlertDialog.Trigger;
export const AlertDialogClose = BaseAlertDialog.Close;

export function AlertDialogContent({ className, children, ...props }: BaseAlertDialog.Popup.Props) {
  return (
    <BaseAlertDialog.Portal>
      <BaseAlertDialog.Backdrop
        className={cn(
          'fixed inset-0 z-(--elevation-overlay) bg-(--color-overlay-scrim)',
          'transition-opacity duration-(--motion-duration-normal) ease-(--motion-easing-standard)',
          'data-[ending-style]:opacity-0 data-[starting-style]:opacity-0',
          'motion-reduce:transition-none',
        )}
      />
      <BaseAlertDialog.Popup
        className={cn(
          'fixed top-1/2 left-1/2 z-(--elevation-dialog) -translate-x-1/2 -translate-y-1/2',
          // A dialog stays inside the viewport with a gutter: a relationship to the window, not a
          // value a token or a brand could decide.
          // biome-ignore lint: documented exception to the tokenised-class rule
          'max-h-[calc(100vh-2rem)] max-w-[calc(100vw-2rem)] overflow-y-auto',
          'w-(--dialog-width-sm) rounded-(--dialog-radius) p-(--dialog-padding)',
          'border border-(--color-border) bg-(--color-surface-elevated) text-(--color-text-primary)',
          'shadow-(--shadow-overlay)',
          // Opacity only, like every other overlay: scaling a surface made of text makes its
          // contents shuffle into place.
          'transition-opacity duration-(--motion-duration-normal) ease-(--motion-easing-standard)',
          'data-[ending-style]:opacity-0 data-[starting-style]:opacity-0',
          'motion-reduce:transition-none',
          className,
        )}
        {...props}
      >
        {children}
      </BaseAlertDialog.Popup>
    </BaseAlertDialog.Portal>
  );
}

export function AlertDialogTitle({ className, ...props }: BaseAlertDialog.Title.Props) {
  return (
    <BaseAlertDialog.Title
      className={cn(
        'text-(length:--font-size-title) font-semibold text-(--color-text-primary)',
        className,
      )}
      {...props}
    />
  );
}

export function AlertDialogDescription({ className, ...props }: BaseAlertDialog.Description.Props) {
  return (
    <BaseAlertDialog.Description
      className={cn(
        'pt-(--space-sm) text-(length:--font-size-body) text-(--color-text-secondary)',
        className,
      )}
      {...props}
    />
  );
}

export function AlertDialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-end gap-(--space-sm) pt-(--space-lg)',
        className,
      )}
      {...props}
    />
  );
}
